import React from "react";
import { Info, AlertCircle } from "lucide-react";
import styles from "./FormField.module.css";

const FormField = ({
  label,
  name,
  required = false,
  tooltip,
  error,
  helperText,
  optionalLabel = false,
  className = "",
  children,
}) => {
  return (
    <div className={`${styles.field} ${className}`.trim()}>
      {(label || tooltip || optionalLabel) && (
        <div className={styles.labelRow}>
          <label htmlFor={name} className={styles.label}>
            <span>{label}</span>
            {required && <span className={styles.required}>*</span>}
            {optionalLabel && !required && (
              <span className={styles.optionalBadge}>(optional)</span>
            )}
            {tooltip && (
              <span className={styles.tooltipWrapper} tabIndex={0} aria-label={tooltip}>
                <Info size={14} />
                <span className={styles.tooltipBubble}>{tooltip}</span>
              </span>
            )}
          </label>
        </div>
      )}

      <div className={styles.controlWrapper}>
        {React.Children.map(children, (child) => {
          if (React.isValidElement(child)) {
            const isDomElement = typeof child.type === "string";
            const extraProps = {
              "aria-invalid": !!error,
              "aria-describedby": error ? `${name}-error` : helperText ? `${name}-helper` : undefined,
            };

            if (name) {
              if (!child.props.id) extraProps.id = name;
              if (!child.props.name && !isDomElement) extraProps.name = name;
            }

            // Only pass 'error' prop to custom React components, not native DOM elements (div, span, etc.)
            if (!isDomElement) {
              extraProps.error = child.props.error !== undefined ? child.props.error : !!error;
            }

            return React.cloneElement(child, extraProps);
          }
          return child;
        })}
      </div>

      {helperText && !error && (
        <p id={`${name}-helper`} className={styles.helperText}>
          {helperText}
        </p>
      )}

      {error && (
        <div id={`${name}-error`} className={styles.errorRow} role="alert">
          <AlertCircle size={13} className={styles.errorIcon} />
          <span className={styles.errorText}>{typeof error === "string" ? error : "Invalid field"}</span>
        </div>
      )}
    </div>
  );
};

export default FormField;
